// Global Instructions Rule Applied! - 2026-02-02

import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import './core/lib/fontawesome'

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications" / "ResizeObserver loop limit exceeded" browser warnings.
// They surface from any descendant that resizes itself in response to a
// layout pass (Jodit editor on unmount inside an AntD Drawer is the most
// common culprit in this app). The browser handles it correctly on the
// next frame; the only effect of leaving it unsuppressed is the CRA /
// react-error-overlay flashing the dev error UI on close.
//
// Implementation notes:
//   * Capture-phase listener so we run BEFORE react-error-overlay's
//     bubbling-phase listener — stopImmediatePropagation only stops
//     listeners later in the chain, so the capture pass is what wins.
//   * Both 'error' and 'unhandledrejection' covered (some browsers route
//     RO loop notifications through one or the other).
//   * We monkey-patch console.error too because react-error-overlay also
//     hooks console.error and would catch the same string from there.
const RESIZE_OBSERVER_LOOP_RX = /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/

const swallowIfResizeLoop = (message: string | undefined | null): boolean => {
  return !!message && RESIZE_OBSERVER_LOOP_RX.test(String(message))
}

window.addEventListener(
  'error',
  (event: ErrorEvent) => {
    if (swallowIfResizeLoop(event.message) || swallowIfResizeLoop(event.error?.message)) {
      event.stopImmediatePropagation()
      event.preventDefault()
    }
  },
  true
)

window.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    const msg = (event.reason && (event.reason.message ?? String(event.reason))) || ''
    if (swallowIfResizeLoop(msg)) {
      event.stopImmediatePropagation()
      event.preventDefault()
    }
  },
  true
)

// react-error-overlay also subscribes to console.error and surfaces those
// as the same dev-overlay error. Filter the noise at the source so the
// overlay never sees the harmless ResizeObserver string.
const originalConsoleError = console.error
console.error = (...args: unknown[]) => {
  for (const arg of args) {
    if (typeof arg === 'string' && swallowIfResizeLoop(arg)) return
    if (arg && typeof arg === 'object' && 'message' in arg && swallowIfResizeLoop((arg as { message?: string }).message)) return
  }
  originalConsoleError.apply(console, args)
}

const root = ReactDOM.createRoot(document.getElementById('root')!)
root.render(<App />)

// Register the PWA service worker (production builds only) so the app is
// installable to the iOS Home Screen and serves an offline app-shell. The
// worker is network-first, so online users always get fresh content; the
// cache is only a fallback when offline. Dev builds skip registration to
// avoid stale-asset caching while iterating.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      // Non-fatal: the app works fine without the SW; just log for diagnostics.
      console.warn('Service worker registration failed:', err)
    })
  })
}
